import { useState, useEffect } from "react";
import axios from "axios";
import {
  UserCheck,
  ClipboardCheck,
  Plus,
  X,
  CheckCircle2,
  XCircle,
  FileText,
  Calendar,
  Eye,
  Loader2,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import PostInterviewEvaluation from "./PostInterviewEvaluation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InterviewCandidates({ jobId, job, allCandidates, onRefresh }) {
  const [interviewCandidates, setInterviewCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingCandidate, setMarkingCandidate] = useState(null);
  
  // Evaluation dialog
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [existingEvaluation, setExistingEvaluation] = useState(null);

  useEffect(() => {
    fetchInterviewCandidates();
  }, [jobId]);

  const fetchInterviewCandidates = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/interview-candidates`);
      setInterviewCandidates(response.data.candidates || []);
    } catch (error) {
      console.error("Error fetching interview candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkForInterview = async (candidate) => {
    setMarkingCandidate(candidate.id);
    try {
      await axios.post(`${API}/jobs/${jobId}/candidates/${candidate.id}/mark-for-interview`);
      toast.success(`${candidate.data?.name || "Candidato"} marcado para entrevista`);
      fetchInterviewCandidates();
    } catch (error) {
      console.error("Error marking candidate:", error);
      toast.error(error.response?.data?.detail || "Error al marcar candidato");
    } finally {
      setMarkingCandidate(null);
    }
  };

  const handleUnmarkForInterview = async (candidateId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}/candidates/${candidateId}/unmark-interview`);
      toast.success("Candidato removido de entrevistas");
      fetchInterviewCandidates();
    } catch (error) {
      console.error("Error unmarking candidate:", error);
      toast.error(error.response?.data?.detail || "Error al remover candidato");
    }
  };

  const handleOpenEvaluation = async (interviewRecord) => {
    setSelectedCandidate(interviewRecord.candidate);
    
    // Check if there's an existing evaluation
    if (interviewRecord.has_evaluation) {
      try {
        const response = await axios.get(
          `${API}/jobs/${jobId}/candidates/${interviewRecord.candidate_id}/evaluation`
        );
        setExistingEvaluation(response.data.evaluation);
      } catch (error) {
        console.error("Error fetching evaluation:", error);
        setExistingEvaluation(null);
      }
    } else {
      setExistingEvaluation(null);
    }
    
    setEvaluationOpen(true);
  };

  const handleEvaluationComplete = () => {
    fetchInterviewCandidates();
    if (onRefresh) onRefresh();
  };

  // Get candidates not yet marked for interview
  const availableCandidates = allCandidates.filter(
    (c) => !interviewCandidates.some((ic) => ic.candidate_id === c.id)
  );

  const getDecisionBadge = (decision) => {
    if (!decision) return null;
    
    const configs = {
      hire: { label: "Contratar", color: "bg-green-100 text-green-800" },
      hire_with_plan: { label: "Con Plan", color: "bg-blue-100 text-blue-800" },
      no_hire: { label: "No Contratar", color: "bg-red-100 text-red-800" },
    };
    
    const config = configs[decision] || { label: decision, color: "bg-gray-100" };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Candidatos en Entrevista</CardTitle>
                <CardDescription>
                  {interviewCandidates.length} candidato(s) en proceso de entrevista
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Interview Candidates List */}
          {interviewCandidates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay candidatos marcados para entrevista</p>
              <p className="text-sm mt-1">Selecciona candidatos de la lista para agregarlos</p>
            </div>
          ) : (
            <div className="space-y-3">
              {interviewCandidates.map((record) => (
                <div
                  key={record.candidate_id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Score */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      (record.candidate?.match_score?.overall_score || 0) >= 80 ? "bg-green-500" :
                      (record.candidate?.match_score?.overall_score || 0) >= 60 ? "bg-amber-500" : "bg-red-500"
                    }`}>
                      {record.candidate?.match_score?.overall_score || 0}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {record.candidate?.data?.name || "Candidato"}
                        </span>
                        {record.has_evaluation && record.evaluation_summary && (
                          getDecisionBadge(record.evaluation_summary.preliminary_decision)
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>
                          Agregado: {new Date(record.marked_for_interview_at).toLocaleDateString("es-PA")}
                        </span>
                        {record.has_evaluation && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Evaluado
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={record.has_evaluation ? "outline" : "default"}
                      onClick={() => handleOpenEvaluation(record)}
                    >
                      {record.has_evaluation ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Evaluación
                        </>
                      ) : (
                        <>
                          <ClipboardCheck className="w-4 h-4 mr-2" />
                          Evaluar
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnmarkForInterview(record.candidate_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Candidates Section */}
          {availableCandidates.length > 0 && (
            <>
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar Candidatos a Entrevista
                </p>
                <div className="grid gap-2 max-h-60 overflow-y-auto">
                  {availableCandidates.slice(0, 10).map((candidate) => (
                    <div
                      key={candidate.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                          (candidate.match_score?.overall_score || 0) >= 80 ? "bg-green-500" :
                          (candidate.match_score?.overall_score || 0) >= 60 ? "bg-amber-500" : "bg-red-500"
                        }`}>
                          {candidate.match_score?.overall_score || 0}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{candidate.data?.name || "Candidato"}</p>
                          <p className="text-xs text-muted-foreground">
                            {candidate.match_score?.match_level || "Pendiente"}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkForInterview(candidate)}
                        disabled={markingCandidate === candidate.id}
                      >
                        {markingCandidate === candidate.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-1" />
                            Agregar
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                {availableCandidates.length > 10 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    +{availableCandidates.length - 10} candidatos más disponibles
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Evaluation Dialog */}
      <Dialog open={evaluationOpen} onOpenChange={setEvaluationOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluación Post-Entrevista</DialogTitle>
            <DialogDescription>
              Completa la evaluación del candidato basada en la entrevista
            </DialogDescription>
          </DialogHeader>
          {selectedCandidate && (
            <PostInterviewEvaluation
              candidate={selectedCandidate}
              job={job}
              existingEvaluation={existingEvaluation}
              onClose={() => setEvaluationOpen(false)}
              onEvaluationComplete={handleEvaluationComplete}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
